import React, { useMemo } from "react"
import { Table, Button, Typography, Flex } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useDevice } from "hooks/useDevice"
import styles from "../index.module.css"
import { getDiscountColumns } from "./columns"
import { useDiscounts } from "./hooks/useDiscounts"
import DiscountFormModal from "./modal/DiscountFormModal"
import DeleteConfirmation from "pagesV2/pro/Settings/ProSettings/CustomTemplate/modal/DeleteConfirmation"
import AssignExperiencesModal from "ui/AssignExperiencesModal"
import CustomPagination from "components/common/CustomPagination"
import DiscountMobileList from "./DiscountMobileList"

const { Title, Text } = Typography

export const Discounts = () => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const tripsState = useSelector((state) => state?.trips?.items || [])

  const {
    discounts,
    isLoading,
    isMutating,
    isModalOpen,
    discountToEdit,
    openAddModal,
    closeModal,
    handleSave,
    isDeleteModalOpen,
    discountToDelete,
    handleDeleteConfirm,
    handleDeleteCancel,
    isToggleWarningOpen,
    discountToToggle,
    handleToggleConfirm,
    handleToggleCancel,
    getMenuItems,
    metadata,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    isAssignModalOpen,
    discountToAssign,
    openAssignModal,
    closeAssignModal,
    handleAssignSave
  } = useDiscounts()

  const columns = useMemo(
    () => getDiscountColumns(getMenuItems, isMutating, t, openAssignModal),
    [getMenuItems, isMutating, t, openAssignModal]
  )

  const currentAssignedExperiences = useMemo(() => {
    if (!discountToAssign) return []
    const applicable = discountToAssign.applicable_experiences
    if (applicable === "all") return tripsState.map((t) => ({ id: String(t.id), name: t.title }))
    if (!Array.isArray(applicable)) return []
    const set = new Set(applicable.map(String))
    return tripsState
      .filter((t) => set.has(String(t.id)))
      .map((t) => ({ id: String(t.id), name: t.title }))
  }, [discountToAssign, tripsState])

  return (
    <>
      <div className={styles.container}>
        <Flex justify="space-between" align="center" className="mb-s">
          <Title level={4} style={{ margin: 0 }}>{t("settings.tab_discounts")}</Title>
          <Button color="primary" variant="solid" icon={<PlusOutlined />} onClick={openAddModal}>
            {t("settings.new_discount")}
          </Button>
        </Flex>
        <Text className={styles.description}>
          {t("settings.discounts_description")}
        </Text>
        <div className={styles.tableWrapper}>
          {isMobile ? (
            <DiscountMobileList
              discounts={isLoading ? [] : discounts}
              getMenuItems={getMenuItems}
              isMutating={isMutating}
            />
          ) : (
            <Table
              columns={columns}
              dataSource={isLoading ? undefined : discounts}
              rowKey="id"
              loading={isLoading}
              pagination={false}
              scroll={{ x: "max-content" }}
              locale={isLoading ? { emptyText: <></> } : undefined}
            />
          )}
        </div>
        <CustomPagination
          metadata={metadata}
          pageSize={pageSize}
          total={metadata.total_results || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      <DiscountFormModal
        visible={isModalOpen}
        onCancel={closeModal}
        onSave={handleSave}
        initialValues={discountToEdit}
        isSaving={isMutating}
      />

      <AssignExperiencesModal
        open={isAssignModalOpen}
        onCancel={closeAssignModal}
        onSave={handleAssignSave}
        currentExperiences={currentAssignedExperiences}
        minSelections={1}
      />

      <DeleteConfirmation
        visible={isDeleteModalOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("settings.discount_delete_title")}
        content={
          discountToDelete
            ? <>Are you sure you want to delete <strong>"{discountToDelete.name}"</strong>? This action cannot be undone.</>
            : undefined
        }
        confirmText={t("settings.action_delete")}
        loading={isMutating}
      />

      <DeleteConfirmation
        visible={isToggleWarningOpen}
        onCancel={handleToggleCancel}
        onConfirm={handleToggleConfirm}
        title={
          discountToToggle?.status === "active"
            ? `${t("settings.action_deactivate")} Discount?`
            : `${t("settings.action_activate")} Discount?`
        }
        content={
          discountToToggle?.status === "active"
            ? <>Deactivating <strong>"{discountToToggle.name}"</strong> will prevent new bookings from using this code. Existing bookings that already applied this discount will not be affected.</>
            : <>Activating <strong>"{discountToToggle?.name}"</strong> will allow guests to use this discount code at checkout.</>
        }
        confirmText={
          discountToToggle?.status === "active"
            ? t("settings.action_deactivate")
            : t("settings.action_activate")
        }
        loading={isMutating}
      />
    </>
  )
}
